'use client';

import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '@/lib/utils';
function Tabs(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      orientation: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("group/tabs flex gap-2 data-horizontal:flex-col", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== orientation || $[7] !== props || $[8] !== t2) {
    t3 = <TabsPrimitive.Root data-slot="tabs" data-orientation={orientation} className={t2} {...props} />;
    $[6] = orientation;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
const tabsListVariants = cva('group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none data-[variant=outline]:rounded-none', {
  variants: {
    variant: {
      default: 'bg-transparent',
      line: 'bg-transparent',
      primary: 'bg-transparent',
      outline: 'gap-1 bg-transparent p-0 group-data-horizontal/tabs:h-10'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

/** Genesys tab trigger layout/focus shell layered on colour variants. */
const genesysTabsTriggerShell = cva('relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-0 py-1 text-sm whitespace-nowrap transition-all group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start  disabled:pointer-events-none disabled:opacity-50 font-normal text-foreground hover:text-foreground dark:text-foreground dark:hover:text-hover-foreground has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1');
const tabsTriggerColourBase = cva('transition-[color,box-shadow] duration-200 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4');
function TabsList(t0) {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const variant = t1 === undefined ? "primary" : t1;
  let t2;
  if ($[4] !== className || $[5] !== variant) {
    t2 = cn(tabsListVariants({
      variant
    }), className);
    $[4] = className;
    $[5] = variant;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2 || $[9] !== variant) {
    t3 = <TabsPrimitive.List data-slot="tabs-list" data-variant={variant} className={t2} {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = variant;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function TabsTrigger(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(genesysTabsTriggerShell(), tabsTriggerColourBase(), "group-data-[variant=default]/tabs-list:font-medium group-data-[variant=default]/tabs-list:text-foreground/60 group-data-[variant=default]/tabs-list:hover:text-foreground dark:group-data-[variant=default]/tabs-list:text-muted-foreground dark:group-data-[variant=default]/tabs-list:hover:text-foreground", "group-data-[variant=line]/tabs-list:text-muted-foreground group-data-[variant=line]/tabs-list:hover:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:text-tab-active-foreground group-data-[variant=line]/tabs-list:data-[state=active]:hover:text-activer-foreground", "group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none", "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent", "group-data-[variant=default]/tabs-list:data-[state=active]:bg-background group-data-[variant=default]/tabs-list:data-[state=active]:text-foreground dark:group-data-[variant=default]/tabs-list:data-[state=active]:border-input dark:group-data-[variant=default]/tabs-list:data-[state=active]:bg-input/30 dark:group-data-[variant=default]/tabs-list:data-[state=active]:text-foreground", "after:absolute after:bg-primary after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-px group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:bg-primary group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100", "group-data-[variant=primary]/tabs-list:px-3 group-data-[variant=primary]/tabs-list:bg-input-idle group-data-[variant=primary]/tabs-list:hover:bg-input-hover group-data-[variant=primary]/tabs-list:data-[state=active]:bg-primary group-data-[variant=primary]/tabs-list:data-[state=active]:text-primary-foreground group-data-[variant=primary]/tabs-list:hover:data-[state=active]:text-primary-foreground", "group-data-[variant=outline]/tabs-list:h-10 group-data-[variant=outline]/tabs-list:py-0 group-data-[variant=outline]/tabs-list:shrink-0 group-data-[variant=outline]/tabs-list:rounded-[88px] group-data-[variant=outline]/tabs-list:border-transparent group-data-[variant=outline]/tabs-list:px-5 group-data-[variant=outline]/tabs-list:text-[0.78rem] group-data-[variant=outline]/tabs-list:font-medium group-data-[variant=outline]/tabs-list:shadow-[inset_0_0_0_2px_transparent] group-data-[variant=outline]/tabs-list:hover:shadow-[inset_0_0_0_2px_var(--color-secondary-foreground)] group-data-[variant=outline]/tabs-list:data-[state=active]:shadow-[inset_0_0_0_1.5px_var(--color-primary)] group-data-[variant=outline]/tabs-list:data-[state=active]:bg-primary/30 group-data-[variant=outline]/tabs-list:hover:bg-secondary/30 group-data-[variant=outline]/tabs-list:data-[state=active]:text-primary-foreground group-data-[variant=outline]/tabs-list:data-[state=inactive]:bg-input-idle group-data-[variant=outline]/tabs-list:data-[state=inactive]:hover:bg-input-hover group-data-[variant=outline]/tabs-list:after:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <TabsPrimitive.Trigger data-slot="tabs-trigger" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function TabsContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex-1 text-sm outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <TabsPrimitive.Content data-slot="tabs-content" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };